import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Upload, Wand2, Sparkles, ArrowRight, CheckCircle2, XCircle, Trash2, Clock, Link2 } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: 'Starting...',
  generating_article: 'Writing article...',
  article_done: 'Generating image...',
  generating_image: 'Generating image...',
  done: 'Ready',
  failed: 'Failed',
};

const AdminAITools = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [inputMode, setInputMode] = useState('screenshot'); // 'screenshot', 'url', or 'video'
  const [submitting, setSubmitting] = useState(false);

  // Video embed state
  const [videoTitle, setVideoTitle] = useState('');
  const [videoEmbed, setVideoEmbed] = useState('');

  // Job queue
  const [jobs, setJobs] = useState([]);

  // Fetch all jobs on load
  useEffect(() => {
    fetchJobs();
  }, []);

  // Poll active jobs
  useEffect(() => {
    const hasActive = jobs.some(j => !['done', 'failed'].includes(j.status));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(fetchJobs, 3000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobs]);

  if (!isAuthenticated) {
    navigate('/admin');
    return null;
  }

  const fetchJobs = async () => {
    try {
      const res = await api.get('/admin/ai/jobs');
      setJobs(res.data || []);
    } catch (e) {
      // ignore
    }
  };

  const handleScreenshotUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const handleGenerate = async () => {
    if (inputMode === 'screenshot' && !screenshot) return;
    if (inputMode === 'url' && !sourceUrl.trim()) return;
    if (inputMode === 'video' && (!videoTitle.trim() || !videoEmbed.trim())) return;
    setSubmitting(true);

    try {
      if (inputMode === 'video') {
        // Quick publish video embed as article
        const { createArticle } = await import('@/lib/api');
        await createArticle({
          title: videoTitle,
          content: videoEmbed,
          category: 'Check On Yo Peoples News Feed',
          status: 'published',
        });
        toast.success('Video story published to COYP News Feed!');
        setVideoTitle('');
        setVideoEmbed('');
      } else if (inputMode === 'url') {
        const res = await api.post('/admin/ai/analyze-url', { url: sourceUrl });
        toast.success('Job started from URL! You can submit more or navigate away.');
        setSourceUrl('');
      } else {
        const base64 = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxSize = 800;
            let w = img.width, h = img.height;
            if (w > maxSize || h > maxSize) {
              if (w > h) { h = (h / w) * maxSize; w = maxSize; }
              else { w = (w / h) * maxSize; h = maxSize; }
            }
            canvas.width = w;
            canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.6).split(',')[1]);
          };
          img.src = URL.createObjectURL(screenshot);
        });

        const res = await api.post('/admin/ai/analyze-screenshot', { image_base64: base64 });
        toast.success('Job started! You can submit more or navigate away.');
        setScreenshot(null);
        setScreenshotPreview('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to start generation');
    } finally {
      setSubmitting(false);
    }
  };

  const loadJobIntoEditor = async (jobId) => {
    try {
      const res = await api.get(`/admin/ai/job/${jobId}/content`);
      let content = res.data.content || '';
      const imgUrl = res.data.image_url ? `${BACKEND_URL}${res.data.image_url}` : '';
      if (imgUrl) {
        content = content.replace(/HERO_IMAGE_PLACEHOLDER/g, imgUrl);
      }
      const articleData = {
        title: res.data.title || '',
        content,
        featured_image: imgUrl,
      };
      sessionStorage.setItem('ai_article_draft', JSON.stringify(articleData));
      navigate('/admin/articles/new?from=ai');
    } catch (e) {
      toast.error('Failed to load job content');
    }
  };

  const deleteJob = async (jobId) => {
    try {
      await api.delete(`/admin/ai/job/${jobId}`);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      toast.success('Deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-ai-tools">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles size={24} className="text-yellow-500" />
            AI Story Generator
          </h2>
          <p className="text-gray-500 mt-1">Upload screenshots → stories generate in background → use when ready</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Submit new job */}
          <div className="lg:col-span-1">
            <div className="bg-[#111] border border-[#222] p-5 sticky top-4">
              <p className="text-xs font-bold text-yellow-500 uppercase tracking-wider mb-3">New Story</p>
              
              {/* Input mode tabs */}
              <div className="flex gap-1 mb-4">
                <button
                  onClick={() => setInputMode('screenshot')}
                  className={`flex-1 py-2 text-[10px] font-bold transition-colors ${inputMode === 'screenshot' ? 'bg-yellow-500 text-white' : 'bg-[#222] text-gray-400'}`}
                  data-testid="mode-screenshot"
                >
                  <Upload size={12} className="inline mr-1" /> Screenshot
                </button>
                <button
                  onClick={() => setInputMode('url')}
                  className={`flex-1 py-2 text-[10px] font-bold transition-colors ${inputMode === 'url' ? 'bg-yellow-500 text-white' : 'bg-[#222] text-gray-400'}`}
                  data-testid="mode-url"
                >
                  <Link2 size={12} className="inline mr-1" /> URL
                </button>
                <button
                  onClick={() => setInputMode('video')}
                  className={`flex-1 py-2 text-[10px] font-bold transition-colors ${inputMode === 'video' ? 'bg-yellow-500 text-white' : 'bg-[#222] text-gray-400'}`}
                  data-testid="mode-video"
                >
                  Video
                </button>
              </div>

              {inputMode === 'screenshot' ? (
                <>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#333] hover:border-yellow-500 p-4 text-center cursor-pointer transition-colors mb-4"
                  >
                    {screenshotPreview ? (
                      <img src={screenshotPreview} alt="Screenshot" className="max-h-40 mx-auto object-contain" />
                    ) : (
                      <>
                        <Upload size={24} className="mx-auto text-gray-500 mb-1" />
                        <p className="text-gray-400 text-xs">Upload screenshot</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotUpload}
                    className="hidden"
                    data-testid="screenshot-upload-input"
                  />
                </>
              ) : inputMode === 'url' ? (
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="Paste news URL..."
                  className="w-full px-3 py-3 bg-black border border-[#222] text-white text-sm focus:outline-none focus:border-yellow-500 mb-4"
                  data-testid="url-input"
                />
              ) : (
                <div className="space-y-3 mb-4">
                  <input
                    type="text"
                    value={videoTitle}
                    onChange={(e) => setVideoTitle(e.target.value)}
                    placeholder="Video title..."
                    className="w-full px-3 py-2.5 bg-black border border-[#222] text-white text-sm focus:outline-none focus:border-yellow-500"
                    data-testid="video-title-input"
                  />
                  <textarea
                    value={videoEmbed}
                    onChange={(e) => setVideoEmbed(e.target.value)}
                    placeholder="Paste VAST embed code..."
                    rows={4}
                    className="w-full px-3 py-2.5 bg-black border border-[#222] text-white text-xs font-mono focus:outline-none focus:border-yellow-500 resize-none"
                    data-testid="video-embed-input"
                  />
                </div>
              )}

              {/* Generate button */}
              <button
                onClick={handleGenerate}
                disabled={(inputMode === 'screenshot' ? !screenshot : inputMode === 'url' ? !sourceUrl.trim() : !videoTitle.trim() || !videoEmbed.trim()) || submitting}
                className="w-full py-3 bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-all-btn"
              >
                {submitting ? (
                  <><Loader2 size={16} className="animate-spin" /> {inputMode === 'video' ? 'Publishing...' : 'Submitting...'}</>
                ) : (
                  <><Wand2 size={16} /> {inputMode === 'video' ? 'Publish Video' : 'Generate'}</>
                )}
              </button>
            </div>
          </div>

          {/* Right: Job queue */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Story Queue ({jobs.length})
              </p>
              {jobs.some(j => !['done', 'failed'].includes(j.status)) && (
                <span className="flex items-center gap-1 text-xs text-yellow-500">
                  <Loader2 size={12} className="animate-spin" /> Processing...
                </span>
              )}
            </div>

            {jobs.length === 0 ? (
              <div className="bg-[#111] border border-[#222] p-12 text-center">
                <Sparkles size={32} className="mx-auto text-gray-700 mb-3" />
                <p className="text-gray-500 text-sm">No stories in queue</p>
                <p className="text-gray-600 text-xs mt-1">Upload a screenshot to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className={`bg-[#111] border p-4 transition-colors ${
                      job.status === 'done' ? 'border-green-500/30' :
                      job.status === 'failed' ? 'border-yellow-500/30' :
                      'border-[#222]'
                    }`}
                    data-testid={`job-${job.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Status icon */}
                      <div className="shrink-0 mt-0.5">
                        {job.status === 'done' ? (
                          <CheckCircle2 size={18} className="text-green-500" />
                        ) : job.status === 'failed' ? (
                          <XCircle size={18} className="text-yellow-500" />
                        ) : (
                          <Loader2 size={18} className="text-yellow-500 animate-spin" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold truncate">
                          {job.title || STATUS_LABELS[job.status] || 'Processing...'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${
                            job.status === 'done' ? 'bg-green-500/20 text-green-500' :
                            job.status === 'failed' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-yellow-500/20 text-yellow-500'
                          }`}>
                            {STATUS_LABELS[job.status] || job.status}
                          </span>
                          {job.created_at && (
                            <span className="text-[10px] text-gray-600">
                              {new Date(job.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        {job.error && (
                          <p className="text-yellow-400 text-xs mt-1">{job.error}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {job.status === 'done' && (
                          <button
                            onClick={() => loadJobIntoEditor(job.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-bold hover:bg-green-700 transition-colors"
                            data-testid={`use-job-${job.id}`}
                          >
                            Use <ArrowRight size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => deleteJob(job.id)}
                          className="p-1.5 text-gray-600 hover:text-yellow-500 transition-colors"
                          data-testid={`delete-job-${job.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Image preview for done jobs */}
                    {job.status === 'done' && job.image_url && (
                      <div className="mt-3 relative bg-[#0a0a0a] border border-[#222]" data-testid={`job-thumbnail-wrap-${job.id}`}>
                        <img
                          src={`${BACKEND_URL}${job.image_url}`}
                          alt={job.title}
                          className="w-full max-h-40 object-cover"
                          loading="eager"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAITools;
